/**
 * IndustrySelector — radio group riutilizzabile per la selezione del settore aziendale.
 *
 * Usato in:
 * - Onboarding wizard (Step 0)
 * - Impostazioni azienda
 */

import { clsx } from 'clsx'
import { INDUSTRY_CONFIGS, type IndustrySlug } from '../../lib/industries'

interface IndustrySelectorProps {
  value: IndustrySlug | null | ''
  onChange: (slug: IndustrySlug | null) => void
  className?: string
  /** Mostra opzione "Altro / Generico" per saltare la selezione */
  showOther?: boolean
}

export function IndustrySelector({
  value,
  onChange,
  className,
  showOther = true,
}: IndustrySelectorProps) {
  return (
    <div className={clsx('space-y-2.5', className)} role="radiogroup" aria-label="Seleziona settore aziendale">
      {INDUSTRY_CONFIGS.map((industry) => {
        const selected = value === industry.slug
        return (
          <label
            key={industry.slug}
            className={clsx(
              'flex items-start gap-3.5 rounded-xl border p-4 cursor-pointer transition-all duration-150',
              selected
                ? 'border-[--km-primary]/60 bg-[--km-primary]/8 shadow-[0_0_0_1px_var(--km-primary,#7c3aed)40]'
                : 'border-[--km-border] bg-[--km-surface] hover:border-[--km-primary]/30 hover:bg-[--km-primary]/3',
            )}
          >
            <input
              type="radio"
              name="industry"
              value={industry.slug}
              checked={selected}
              onChange={() => onChange(industry.slug)}
              className="sr-only"
            />
            {/* Radio dot */}
            <div
              className={clsx(
                'mt-0.5 flex-shrink-0 w-4.5 h-4.5 rounded-full border-2 flex items-center justify-center transition-colors',
                selected
                  ? 'border-[--km-primary] bg-[--km-primary]'
                  : 'border-[--km-border]',
              )}
              aria-hidden="true"
            >
              {selected && (
                <div className="w-1.5 h-1.5 rounded-full bg-white" />
              )}
            </div>

            {/* Emoji */}
            <span className="text-xl leading-none mt-0.5 select-none" aria-hidden="true">
              {industry.emoji}
            </span>

            {/* Testo */}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className={clsx(
                  'text-sm font-semibold',
                  selected ? 'text-[--km-primary]' : 'text-[--km-text-primary]',
                )}>
                  {industry.label}
                </span>
                <span className="text-xs text-[--km-text-muted]">
                  {industry.tagline}
                </span>
              </div>
              <p className="text-xs text-[--km-text-secondary] mt-1 leading-relaxed">
                {industry.description}
              </p>
              {industry.examples.length > 0 && (
                <p className="text-xs text-[--km-text-muted] mt-1">
                  Es.: {industry.examples.slice(0, 2).join(', ')}
                </p>
              )}
            </div>
          </label>
        )
      })}

      {showOther && (
        <label
          className={clsx(
            'flex items-center gap-3.5 rounded-xl border p-4 cursor-pointer transition-all duration-150',
            value === null || value === ''
              ? 'border-[--km-border] bg-[--km-surface-alt]'
              : 'border-[--km-border] bg-[--km-surface] hover:border-[--km-border] hover:bg-[--km-surface-alt]',
          )}
        >
          <input
            type="radio"
            name="industry"
            value=""
            checked={value === null || value === ''}
            onChange={() => onChange(null)}
            className="sr-only"
          />
          <div
            className={clsx(
              'flex-shrink-0 w-4.5 h-4.5 rounded-full border-2 flex items-center justify-center transition-colors mt-0.5',
              value === null || value === ''
                ? 'border-[--km-text-muted] bg-[--km-text-muted]'
                : 'border-[--km-border]',
            )}
            aria-hidden="true"
          >
            {(value === null || value === '') && (
              <div className="w-1.5 h-1.5 rounded-full bg-white" />
            )}
          </div>
          <span className="text-xl select-none" aria-hidden="true">⚙️</span>
          <div>
            <span className="text-sm font-medium text-[--km-text-secondary]">
              Altro / Configurazione manuale
            </span>
            <p className="text-xs text-[--km-text-muted] mt-0.5">
              Salta la pre-selezione e configura manualmente il mapping dei tuoi file.
            </p>
          </div>
        </label>
      )}
    </div>
  )
}
